import React, { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { getToken, getUser } from '../lib/api'
import { hasPermission } from '../data/users'
import type { UserRole } from '../data/users'
import type { User } from '../types/index'
import Layout from './Layout'

interface AuthenticatedRouteProps {
  children: React.ReactNode
  requiredPermission: 'dashboard' | 'orders' | 'timesheets' | 'reports' | 'settings'
  fallbackPath?: string
}

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
  requiredPermission,
  fallbackPath = '/timesheet'
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUserState] = useState<Pick<User, 'id' | 'username' | 'role'> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    const token = getToken()
    const savedUser = getUser()
    
    if (token && savedUser) {
      setIsAuthenticated(true)
      setUserState(savedUser)
    }
    
    setLoading(false)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUserState(null)
    setIsAuthenticated(false)
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ color: '#666', fontSize: '1.2rem' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check if user has the required permission
  if (!user || !hasPermission(user.role as UserRole, requiredPermission)) {
    return <Navigate to={fallbackPath} replace />
  }

  return (
    <Layout onLogout={handleLogout} user={user}>
      {children}
    </Layout>
  )
}

export default AuthenticatedRoute 